import { useMemo } from 'react';
import { useErrorHandlerContext, useUserLanguage, useListNavigationStateContext } from '@/customHooks';
import { capitalizeFirstLetter, formatIdWithTooltip } from '@/helpers';
import { OwnerContractFilterInput, useGetOwnerContractsForListQuery, GetOwnerContractsForListQuery } from '@/graphql/generated';
import { ButtonWithDropDownMenu } from '../molecules';
import { ContractStatusPill, LoadingSpinner, OwnerContractLeaseTypePill } from '../atoms';
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import textsData from '@/textData/CreateContractsTexts.json';

type OwnerContractsForList = NonNullable<GetOwnerContractsForListQuery['ownerContracts']>['items'][number]

type OwnerContractListTableProps = {
  filters: OwnerContractFilterInput;
};

const readableDate = (date: string | null | undefined) => {
  if (!date) return '-'
  const dateObj = new Date(date)
  return dateObj.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const dataFlatter = (data: OwnerContractsForList[] | undefined) => {
  if (!data) return []

  return data.map((element) => {
    const ownerName = element.owner
      ? `${capitalizeFirstLetter(element.owner.firstName)} ${capitalizeFirstLetter(element.owner.lastName)}`
      : 'Sin Propietario'
    const propertyName = element.property?.name ? capitalizeFirstLetter(element.property.name) : 'No Applicable'
    
    return {
      id: element.id,
      ownerName,
      propertyName,
      leaseType: element.ownerContractLeaseTypeOption,
      startDate: readableDate(element.startDate),
      endDate: readableDate(element.finishDate),
      contractAmount: element.contractAmount,
      status: element.contractStatusOption,
    }
  })
}

export const OwnerContractListTable = ({ filters }: OwnerContractListTableProps) => {
  const userLanguage = useUserLanguage()
  const rootTexts = userLanguage === 'es' ? textsData.es : textsData.en;
  const texts = rootTexts.ownerContract.list;

  const navigate = useNavigate()
  const { setListState } = useListNavigationStateContext()
  const { open: openErrorModal } = useErrorHandlerContext()

  const { data, error, loading } = useGetOwnerContractsForListQuery({
    variables: { filterInput: filters },
  })

  const flattenedData = useMemo(
    () => (data ? dataFlatter(data.ownerContracts?.items as OwnerContractsForList[]) : null),
    [data, userLanguage]
  )

  if (error) openErrorModal(error)

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <LoadingSpinner text />
      </div>
    )
  }

  if (!flattenedData || flattenedData.length === 0) {
    return (
      <div className="px-6 py-6">
        <span>
          <b>{texts.noData}</b>
        </span>
      </div>
    )
  }

  return (
    <div className="py-6">
      <div className="overflow-x-auto rounded-xl grid grid-cols-[1fr_2fr_2fr_200px_130px_1fr_1fr_1fr_64px] content-start w-full min-w-[1000px]">
        {/* Header */}
        <div className="contents">
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {texts.columns.contractId}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {texts.columns.owner}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {texts.columns.property}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {texts.columns.leaseTerm}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {userLanguage === 'es' ? 'Estado' : 'Status'}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {userLanguage === 'es' ? 'Inicio' : 'Start'}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {userLanguage === 'es' ? 'Fin' : 'End'}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {userLanguage === 'es' ? 'Monto' : 'Amount'}
          </div>
          <div className="border-t border-b border-gray-500 bg-white sticky top-0 z-10"></div>
        </div>

        {/* Rows */}
        {flattenedData.map((contract) => {
          const { short, full } = formatIdWithTooltip(contract.id)
          return (
            <div key={contract.id} className="contents group">
              <div
                className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white"
                title={full}
              >
                {short}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                {contract.ownerName}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                {contract.propertyName}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                <OwnerContractLeaseTypePill type={contract.leaseType} />
              </div>
              <div className="p-[15px] flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                <ContractStatusPill status={contract.status} />
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                {contract.startDate}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                {contract.endDate}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                {contract.contractAmount}
              </div>
              <div className="p-[15px] flex items-center justify-end border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                <ButtonWithDropDownMenu
                  options={[
                    {
                      item: 'Detalles',
                      action: () => {
                        setListState('ownerContracts', {
                          currentPage: parseInt(filters.page || '1'),
                          filters,
                          selectedItemId: contract.id,
                        })

                        navigate(
                          `/${pathURL.USER}/${pathURL.CONTRACT}/${pathURL.OWNER}/${pathURL.DETAIL}`,
                          { state: { id: contract.id } }
                        )
                      },
                    },
                  ]}
                  position="left"
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

